/**
 * Shared file-type descriptor resolver (LF-4642).
 *
 * Unifies the three duplicated file-icon implementations that grew up across
 * the app — `attachmentKind()` in `communications/attachment-chip`,
 * `getFileIcon()` in `notifications-workflow/add-attachment-dialog`, and
 * `isImageFile()` in the former `ui/file-upload-field` (since removed) — behind a single resolver.
 *
 * Given a MIME type and/or filename it returns:
 *  - `kind`    — a coarse classification (pdf/image/word/…);
 *  - `label`   — a short uppercase label for the monochrome file-type tile the
 *                `FileDropZone` list renders (LF-4642 muted pass: the label
 *                carries the type, colour stays out of the list);
 *  - `iconSrc` — the path to the coloured file-type SVG in `/public` for
 *                callers that still want the glyph (e.g. the comms attachment
 *                chip once it migrates), or `null` for the generic fallback.
 *
 * MIME + extension based (not Vale document-type based) — deliberately the same
 * axis as the old `attachmentKind`, which was the most complete of the three.
 */

export type FileKind =
  | 'pdf'
  | 'image'
  | 'word'
  | 'spreadsheet'
  | 'email'
  | 'audio'
  | 'video'
  | 'archive'
  | 'other';

export interface FileTypeDescriptor {
  kind: FileKind;
  /** Short uppercase label for the file-type tile, e.g. 'PDF', 'DOCX', 'JPG'. */
  label: string;
  /** Path to the coloured file-type SVG in /public, or null for the fallback. */
  iconSrc: string | null;
}

/** The five file-type SVGs that ship in `public/icons/file-types/`. */
const KIND_SVG: Partial<Record<FileKind, string>> = {
  pdf: '/icons/file-types/pdf2.svg',
  word: '/icons/file-types/word2.svg',
  spreadsheet: '/icons/file-types/excel2.svg',
  image: '/icons/file-types/image.svg',
  email: '/icons/file-types/mailing.svg'
};

/** Fallback labels when a filename has no usable extension. */
const KIND_FALLBACK_LABEL: Record<FileKind, string> = {
  pdf: 'PDF',
  image: 'IMG',
  word: 'DOC',
  spreadsheet: 'XLS',
  email: 'EML',
  audio: 'AUDIO',
  video: 'VIDEO',
  archive: 'ZIP',
  other: 'FILE'
};

function classifyKind(mime: string, filename: string): FileKind {
  if (mime === 'application/pdf' || /\.pdf$/i.test(filename)) return 'pdf';
  if (
    mime.startsWith('image/') ||
    /\.(jpe?g|png|gif|webp|heic|heif|svg)$/i.test(filename)
  )
    return 'image';
  if (
    mime === 'application/msword' ||
    mime ===
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    /\.docx?$/i.test(filename)
  ) {
    return 'word';
  }
  if (
    mime === 'application/vnd.ms-excel' ||
    mime ===
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    mime === 'text/csv' ||
    /\.(xlsx?|csv)$/i.test(filename)
  ) {
    return 'spreadsheet';
  }
  if (mime === 'message/rfc822' || /\.(eml|msg)$/i.test(filename))
    return 'email';
  if (
    mime.startsWith('audio/') ||
    /\.(mp3|wav|m4a|ogg|aac|flac)$/i.test(filename)
  )
    return 'audio';
  if (
    mime.startsWith('video/') ||
    /\.(mp4|mov|webm|mkv|avi|m4v)$/i.test(filename)
  )
    return 'video';
  if (
    /^application\/(zip|x-zip-compressed|x-rar-compressed|x-7z-compressed)$/.test(
      mime
    ) ||
    /\.(zip|rar|7z)$/i.test(filename)
  )
    return 'archive';
  return 'other';
}

/** The extension, uppercased and capped, preferred over a generic kind label. */
function deriveLabel(kind: FileKind, filename: string): string {
  const ext = filename.split('.').pop()?.toUpperCase();
  if (ext && ext !== filename.toUpperCase() && ext.length <= 5) {
    // Normalise the common Office long-forms to their familiar short label.
    if (ext === 'JPEG') return 'JPG';
    return ext;
  }
  return KIND_FALLBACK_LABEL[kind];
}

/**
 * Resolve a file's display descriptor from its MIME type and filename.
 * Both are optional — pass whatever is available (a dropped `File` has both).
 */
export function getFileTypeDescriptor(
  mimeType?: string | null,
  filename = ''
): FileTypeDescriptor {
  const mime = (mimeType ?? '').toLowerCase();
  const kind = classifyKind(mime, filename);
  return {
    kind,
    label: deriveLabel(kind, filename),
    iconSrc: KIND_SVG[kind] ?? null
  };
}
